import { Box, Group, Stack, Text, Title } from "@mantine/core";
import {
  IconBarcode,
  IconListNumbers,
  IconPhoto,
  IconQrcode,
  IconTable,
  IconTypography,
} from "@tabler/icons-react";
import type { ParseKeys } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

type FeatureKind =
  | "text"
  | "barcode"
  | "qr"
  | "numbers"
  | "variable"
  | "images";

const BAR_PATTERN = [
  2, 1, 1, 2, 1, 3, 1, 1, 2, 1, 1, 2, 3, 1, 1, 1, 2, 1, 2, 1, 1, 3, 1, 2, 1, 1,
  2, 1, 1, 2, 1, 2, 3, 1, 1, 2, 1, 1, 1, 2,
];

function MiniBarcode({ className }: { className?: string }) {
  return (
    <div className={className ?? "feature-mini-barcode"} aria-hidden>
      {BAR_PATTERN.map((w, i) => (
        <span key={i} style={{ flex: w }} />
      ))}
    </div>
  );
}

function buildDecorativeQr(size = 21): boolean[] {
  const grid = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => false),
  );

  const inFinder = (r: number, c: number) =>
    (r < 8 && c < 8) ||
    (r < 8 && c >= size - 8) ||
    (r >= size - 8 && c < 8);

  const placeFinder = (row: number, col: number) => {
    for (let i = 0; i < 7; i++) {
      for (let j = 0; j < 7; j++) {
        const border = i === 0 || i === 6 || j === 0 || j === 6;
        const center = i >= 2 && i <= 4 && j >= 2 && j <= 4;
        grid[row + i][col + j] = border || center;
      }
    }
  };

  placeFinder(0, 0);
  placeFinder(0, size - 7);
  placeFinder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    grid[6][i] = i % 2 === 0;
    grid[i][6] = i % 2 === 0;
  }

  // Dark module near bottom-left finder (QR convention)
  grid[size - 8][8] = true;

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (inFinder(r, c) || r === 6 || c === 6) continue;
      // Dense, deterministic fill so it reads as a QR code
      grid[r][c] = ((r * 5 + c * 3 + (r * c) % 7) % 3) !== 0;
    }
  }

  return grid.flat();
}

const QR_CELLS = buildDecorativeQr(21);

function QrBlock() {
  return (
    <div className="feature-qr" aria-hidden>
      {QR_CELLS.map((on, i) => (
        <span key={i} className={on ? "on" : undefined} />
      ))}
    </div>
  );
}

function FeaturePreview({ kind }: { kind: FeatureKind }) {
  const { t } = useTranslation();

  switch (kind) {
    case "text":
      return (
        <div className="feature-label feature-label--text">
          <Text size="xs" c="dimmed">
            {t("marketing.featureDemo.productName")}
          </Text>
          <Text fw={700} fz={18} c="charcoal.8" lh={1.2}>
            {t("marketing.featureDemo.oliveOil")}
          </Text>
          <Text size="sm" c="charcoal.6">
            {t("marketing.featureDemo.oliveOilDesc")}
          </Text>
          <div className="feature-label-divider" />
          <Text
            size="sm"
            c="charcoal.5"
            fs="italic"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {t("marketing.featureDemo.fontsNote")}
          </Text>
        </div>
      );
    case "barcode":
      return (
        <div className="feature-label feature-label--barcode">
          <div className="feature-barcode-stack">
            <MiniBarcode className="feature-barcode-large" />
            <Text
              size="xs"
              ta="center"
              c="charcoal.7"
              ff="monospace"
              className="feature-barcode-digits"
            >
              1234456789906738
            </Text>
          </div>
        </div>
      );
    case "qr":
      return (
        <div className="feature-label feature-label--qr">
          <QrBlock />
          <Text size="xs" ta="center" c="charcoal.6" mt={8}>
            www.website.com
          </Text>
        </div>
      );
    case "numbers":
      return (
        <div className="feature-label feature-label--numbers">
          {[1, 2, 3].map((n) => (
            <div key={n} className="feature-seq-card">
              <Text size="xs" c="dimmed" ta="center" w="100%">
                {t("marketing.featureDemo.sequence", { n })}
              </Text>
              <Text fw={800} fz={22} c="charcoal.8" lh={1} ta="center">
                {String(n).padStart(3, "0")}
              </Text>
              <MiniBarcode />
            </div>
          ))}
        </div>
      );
    case "variable":
      return (
        <div className="feature-label feature-label--variable">
          <div className="feature-var-row">
            <span>{t("marketing.featureDemo.client")}</span>
            <span className="feature-var-token">
              [{t("marketing.featureDemo.name")}]
            </span>
          </div>
          <div className="feature-var-row">
            <span>{t("marketing.featureDemo.city")}</span>
            <span className="feature-var-token">[Madrid]</span>
          </div>
          <div className="feature-var-row">
            <span>{t("marketing.featureDemo.client")}</span>
            <span className="feature-var-token">[Mario]</span>
          </div>
        </div>
      );
    case "images":
      return (
        <div className="feature-label feature-label--images">
          <Group gap="sm" align="center" wrap="nowrap">
            <div className="feature-logo-mark" aria-hidden>
              <svg viewBox="0 0 40 40" width="40" height="40">
                <circle cx="20" cy="20" r="19" fill="#f27a21" opacity="0.15" />
                <path
                  d="M20 8c-2.5 4-6 7-6 12a6 6 0 0012 0c0-5-3.5-8-6-12z"
                  fill="#f27a21"
                />
                <path
                  d="M14 22c1.5 3 3.5 5 6 5s4.5-2 6-5"
                  stroke="#c45a10"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
            </div>
            <Text fw={700} fz={15} c="charcoal.8">
              {t("marketing.featureDemo.companyLogo")}
            </Text>
          </Group>
          <div className="feature-logo-wave" aria-hidden />
        </div>
      );
  }
}

const FEATURES: {
  kind: FeatureKind;
  titleKey: ParseKeys;
  icon: React.ReactNode;
  to?: string;
}[] = [
  {
    kind: "text",
    titleKey: "marketing.featureText",
    icon: <IconTypography size={22} stroke={1.6} />,
  },
  {
    kind: "barcode",
    titleKey: "marketing.featureBarcode",
    icon: <IconBarcode size={22} stroke={1.6} />,
    to: "/barcodes",
  },
  {
    kind: "qr",
    titleKey: "marketing.featureQr",
    icon: <IconQrcode size={22} stroke={1.6} />,
    to: "/barcodes",
  },
  {
    kind: "numbers",
    titleKey: "marketing.featureNumbers",
    icon: <IconListNumbers size={22} stroke={1.6} />,
  },
  {
    kind: "variable",
    titleKey: "marketing.featureVariable",
    icon: <IconTable size={22} stroke={1.6} />,
  },
  {
    kind: "images",
    titleKey: "marketing.featureImages",
    icon: <IconPhoto size={22} stroke={1.6} />,
  },
];

function FeatureCardBody({
  kind,
  title,
  icon,
}: {
  kind: FeatureKind;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    <Stack className="marketing-feature feature-card" gap="md" p="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Title order={4} c="charcoal.8" lh={1.25} maw="80%">
          {title}
        </Title>
        <span className="feature-card-icon">{icon}</span>
      </Group>
      <FeaturePreview kind={kind} />
    </Stack>
  );
}

export function FeatureLabelCards() {
  const { t } = useTranslation();

  return (
    <Box className="feature-cards-grid">
      {FEATURES.map((f) => {
        const title = t(f.titleKey);

        if (!f.to) {
          return (
            <FeatureCardBody
              key={f.kind}
              kind={f.kind}
              title={title}
              icon={f.icon}
            />
          );
        }

        return (
          <Link
            key={f.kind}
            to={f.to}
            className="feature-card-link"
            aria-label={`${title}. ${t("marketing.seeBarcodeTypes")}`}
          >
            <FeatureCardBody kind={f.kind} title={title} icon={f.icon} />
          </Link>
        );
      })}
    </Box>
  );
}
